import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import {
  Car, MapPin, Calendar, Clock, Users, DollarSign, Loader2,
  CheckCircle, XCircle, AlertCircle, CreditCard, Phone, Mail,
  Plane, FileText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

const QuoteAcceptPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [bookingRef, setBookingRef] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchQuote();
  }, [token]);

  useEffect(() => {
    // If returning from Stripe with session_id, confirm payment
    if (sessionId && quote && quote.status === "accepted") {
      confirmPayment();
    }
  }, [sessionId, quote]);

  const fetchQuote = async () => {
    try {
      const response = await api.get(`/quotes/view/${token}`);
      setQuote(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Quote not found or has expired");
    } finally {
      setLoading(false);
    }
  };

  const confirmPayment = async () => {
    if (processing || paymentSuccess) return;
    
    setProcessing(true);
    try {
      const response = await api.post(`/quotes/confirm-payment/${token}?session_id=${sessionId}`);
      setPaymentSuccess(true);
      setBookingRef(response.data.booking_ref);
      toast.success("Payment confirmed! Your booking is confirmed.");
    } catch (err) {
      if (err.response?.data?.detail?.includes("already")) {
        setPaymentSuccess(true);
        setBookingRef(err.response?.data?.booking_ref);
      } else {
        toast.error(err.response?.data?.detail || "Failed to confirm payment");
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleAcceptQuote = async () => {
    setProcessing(true);
    try {
      const response = await api.post(`/quotes/checkout/${token}`);
      console.log("Checkout response:", response.data);
      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Unable to create payment session. Please try again.");
        setProcessing(false);
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error(err.response?.data?.detail || "Failed to process payment");
      setProcessing(false);
    }
  };

  const handleDeclineQuote = async () => {
    if (!window.confirm("Are you sure you want to decline this quote?")) return;
    
    setProcessing(true);
    try {
      await api.post(`/quotes/decline/${token}`);
      setQuote(prev => ({ ...prev, status: "declined" }));
      toast.success("Quote declined");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to decline quote");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-16">
          <Card className="dashboard-card text-center">
            <CardContent className="pt-8 pb-8">
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="h-10 w-10 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold mb-4">Quote Not Available</h1>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Link to="/book">
                <Button>Create New Booking</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Payment success view
  if (paymentSuccess || sessionId) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-16">
          <Card className="dashboard-card text-center" data-testid="payment-success">
            <CardContent className="pt-8 pb-8">
              {processing ? (
                <>
                  <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-6" />
                  <h1 className="text-2xl font-bold mb-4">Processing Payment...</h1>
                  <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-green-500" />
                  </div>
                  <h1 className="text-3xl font-bold mb-2">Booking Confirmed!</h1>
                  <p className="text-muted-foreground mb-6">Your transfer has been booked successfully.</p>
                  
                  {bookingRef && (
                    <div className="text-4xl font-mono font-bold text-primary mb-6">
                      {bookingRef}
                    </div>
                  )}
                  
                  <p className="text-sm text-muted-foreground mb-8">
                    A confirmation email has been sent to <span className="text-foreground">{quote?.passenger_email}</span>
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to={`/track/${bookingRef}`}>
                      <Button>Track Your Booking</Button>
                    </Link>
                    <Link to="/">
                      <Button variant="outline">Back to Home</Button>
                    </Link>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Status-based views
  const StatusBadge = () => {
    const statusConfig = {
      pending: { color: "bg-yellow-500/20 text-yellow-500", icon: Clock, text: "Pending" },
      accepted: { color: "bg-blue-500/20 text-blue-500", icon: CheckCircle, text: "Accepted" },
      declined: { color: "bg-red-500/20 text-red-500", icon: XCircle, text: "Declined" },
      expired: { color: "bg-gray-500/20 text-gray-500", icon: AlertCircle, text: "Expired" },
      paid: { color: "bg-green-500/20 text-green-500", icon: CheckCircle, text: "Paid" }
    };
    
    const config = statusConfig[quote.status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1 px-3 py-1`}>
        <Icon className="h-3 w-3" />
        {config.text}
      </Badge>
    );
  };

  const pickupDate = new Date(quote.pickup_datetime);
  const expiresDate = new Date(quote.expires_at);
  const cardFee = quote.price * 0.022;
  const totalWithFee = quote.price + cardFee;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Quote Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Your Transfer Quote</h1>
          <div className="flex items-center justify-center gap-3">
            <span className="text-2xl font-mono text-primary">{quote.quote_ref}</span>
            <StatusBadge />
          </div>
        </div>

        {/* Declined/Expired Message */}
        {(quote.status === "declined" || quote.status === "expired") && (
          <Card className="dashboard-card mb-6 border-red-500/30">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">
                {quote.status === "declined" ? "Quote Declined" : "Quote Expired"}
              </h2>
              <p className="text-muted-foreground mb-4">
                {quote.status === "declined" 
                  ? "You have declined this quote. Feel free to create a new booking if you change your mind."
                  : "This quote has expired. Please request a new quote or create a new booking."}
              </p>
              <Link to="/book">
                <Button>Create New Booking</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Quote Details Card */}
        <Card className="dashboard-card mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Trip Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Route */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">Pickup</p>
                  <p className="font-medium">{quote.pickup_location}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-red-500 mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">Dropoff</p>
                  <p className="font-medium">{quote.dropoff_location || "As directed"}</p>
                </div>
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-black/10">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="font-medium">{format(pickupDate, "EEEE, MMM d, yyyy")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Time</p>
                  <p className="font-medium">{format(pickupDate, "h:mm a")}</p>
                </div>
              </div>
            </div>

            {/* Vehicle & Passengers */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-black/10">
              <div className="flex items-center gap-3">
                <Car className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Vehicle</p>
                  <p className="font-medium capitalize">{quote.vehicle_type}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Passengers</p>
                  <p className="font-medium">{quote.num_passengers}</p>
                </div>
              </div>
            </div>

            {/* Flight Info */}
            {quote.flight_number && (
              <div className="flex items-center gap-3 pt-4 border-t border-black/10">
                <Plane className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Flight Number</p>
                  <p className="font-medium">{quote.flight_number}</p>
                </div>
              </div>
            )}

            {/* Special Requirements */}
            {quote.special_requirements && (
              <div className="pt-4 border-t border-black/10">
                <p className="text-xs text-muted-foreground mb-1">Special Requirements</p>
                <p className="text-sm">{quote.special_requirements}</p>
              </div>
            )}
            
            {/* Notes */}
            {quote.notes && (
              <div className="pt-4 border-t border-black/10">
                <p className="text-xs text-muted-foreground mb-1">Notes</p>
                <p className="text-sm">{quote.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Passenger Details */}
        <Card className="dashboard-card mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Passenger Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-primary font-bold">{quote.passenger_name.charAt(0)}</span>
              </div>
              <div>
                <p className="font-medium">{quote.passenger_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{quote.passenger_email}</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{quote.passenger_phone}</span>
            </div>
          </CardContent>
        </Card>

        {/* Price Summary */}
        <Card className="dashboard-card mb-6 border-primary/30">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Base Fare</span>
                <span>${quote.price.toFixed(2)}</span>
              </div>
              
              {quote.discount_amount > 0 && (
                <div className="flex justify-between text-green-500">
                  <span>Corporate Discount ({quote.discount_percentage}%)</span>
                  <span>-${quote.discount_amount.toFixed(2)}</span>
                </div>
              )}
              
              {quote.extras_total > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Extras</span>
                  <span>+${quote.extras_total.toFixed(2)}</span>
                </div>
              )}
              
              <div className="border-t border-black/10 my-2"></div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${quote.price.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">Card Processing Fee (2.2%)</span>
                <span>${cardFee.toFixed(2)}</span>
              </div>
              
              <div className="border-t border-black/10 my-2"></div>
              
              <div className="flex justify-between items-center text-xl font-bold">
                <span>Total to Pay</span>
                <span className="text-green-500">${totalWithFee.toFixed(2)} AUD</span>
              </div>
              <p className="text-xs text-muted-foreground text-right">Inc. GST</p>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons - Show for pending or accepted (not yet paid) quotes */}
        {(quote.status === "pending" || quote.status === "accepted") && (
          <div className="space-y-4">
            <Button
              onClick={handleAcceptQuote}
              disabled={processing}
              className="w-full py-6 text-lg bg-primary hover:bg-primary/90"
              data-testid="accept-quote-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-5 w-5" />
                  {quote.status === "accepted" ? "Complete Payment" : "Accept & Pay"} ${totalWithFee.toFixed(2)}
                </>
              )}
            </Button>
            
            {quote.status === "pending" && (
              <Button
                onClick={handleDeclineQuote}
                disabled={processing}
                variant="outline"
                className="w-full"
                data-testid="decline-quote-btn"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Decline Quote
              </Button>
            )}
            
            <p className="text-xs text-center text-muted-foreground">
              This quote expires on {format(expiresDate, "MMMM d, yyyy")}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

// Header Component
const Header = () => (
  <header className="glass sticky top-0 z-10 border-b border-white/5">
    <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10" />
        <span className="font-bold text-xl">Limo Near Me</span>
      </Link>
      <Link to="/login">
        <Button variant="outline" size="sm">Login</Button>
      </Link>
    </div>
  </header>
);

export default QuoteAcceptPage;
